import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download } from "lucide-react";
import api from "@/lib/api";
import { SectionTitle, Card, SaveButton } from "../ui";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50";

const TEXT_FIELDS = [
  ["google_review_link", "Google review link (your Google Business review URL)"],
  ["google_profile_url", "Google Business profile URL (empty = hide button)"],
  ["yelp_page_url", "Yelp page URL (empty = hide button)"],
  ["company_name", "Company name"],
  ["tagline", "Tagline"],
  ["site_url", "Website URL"],
  ["sender_name", "Email sender name"],
  ["reply_to", "Reply-to email (blank = default)"],
  ["contact_email", "Contact email"],
  ["support_email", "Support email"],
  ["whatsapp", "WhatsApp number"],
  ["footer_info", "Footer business info"],
];

const VARIANTS = [
  ["primary", "Primary logo", "Used on documents & the site (full logo with wordmark).", "/brand/logo.png"],
  ["mark", "Logo mark", "Small circular mark — site header, footer, favicons base.", "/brand/logo-mark.png"],
  ["email", "Email-safe logo", "Shown in every email header (hosted on your domain).", "/brand/logo-email.png"],
  ["dark", "Dark-background version", "For dark sections and footers.", "/brand/logo-dark.png"],
  ["pdf", "PDF / document logo", "Embedded in quotes, confirmations, and all generated PDFs.", ""],
];

export default function BrandManager() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [bust, setBust] = useState(Date.now());
  const fileRef = useRef(null);
  const [target, setTarget] = useState(null);

  useEffect(() => { api.get("/admin/brand-settings").then((r) => setB(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/brand-settings", b);
      setB(data);
      toast.success("Brand settings saved — all future emails, PDFs and documents use them now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const pick = (variant) => { setTarget(variant); fileRef.current?.click(); };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !target) return;
    setUploading(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/brand-logo/${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated — it's live everywhere it's used");
      setBust(Date.now());
      api.get("/admin/brand-settings").then((r) => setB(r.data));
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading("");
    setTarget(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const [downloading, setDownloading] = useState(false);
  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/db-export", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const name = match ? match[1] : `backup-${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    setDownloading(false);
  };

  if (!b) return <p className="text-charcoal-500">Loading…</p>;

  return (
    <div data-testid="admin-brand">
      <SectionTitle title="Brand assets" subtitle="One source of truth. Everything here auto-syncs to all emails, PDFs, quotes, confirmations, and the site — no code edits." />
      <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={upload} className="hidden" data-testid="brand-file-input" />
      <div className="space-y-6">
        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Logos</h3>
          <p className="text-xs text-charcoal-500 mb-4">Upload once, reused everywhere. If a variant is missing, the primary mark is used as fallback.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {VARIANTS.map(([id, label, hint, publicPath]) => (
              <div key={id} className="flex items-center gap-4 rounded-xl border border-sand-200 p-4" data-testid={`brand-logo-${id}`}>
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sand-100 overflow-hidden">
                  {publicPath ? <img src={`${publicPath}?v=${bust}`} alt={label} className="max-h-14 max-w-14 object-contain"
                    onError={(e) => { e.target.style.display = "none"; }} />
                    : <span className="text-[10px] text-charcoal-400 text-center px-1">PDF asset</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-charcoal-800">{label}</p>
                  <p className="text-xs text-charcoal-500">{hint}</p>
                </div>
                <button data-testid={`brand-upload-${id}`} onClick={() => pick(id)} disabled={!!uploading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-3.5 py-1.5 text-xs font-medium text-charcoal-700 hover:border-terracotta disabled:opacity-60">
                  <Upload size={12} /> {uploading === id ? "Uploading…" : "Replace"}
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-4">Identity & correspondence</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {TEXT_FIELDS.map(([k, label]) => (
              <label key={k} className="block text-sm text-charcoal-600">{label}
                <input data-testid={`brand-${k}`} className={`${FIELD} mt-1`} value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })} /></label>
            ))}
          </div>
          <div className="mt-4 space-y-4">
            <label className="block text-sm text-charcoal-600">Extra email footer line (optional)
              <input data-testid="brand-email_footer_text" className={`${FIELD} mt-1`} value={b.email_footer_text || ""} onChange={(e) => setB({ ...b, email_footer_text: e.target.value })} /></label>
            <label className="block text-sm text-charcoal-600">Document footer / disclaimer (all PDFs)
              <textarea data-testid="brand-document_footer_text" rows={2} className={`${FIELD} mt-1`} value={b.document_footer_text || ""} onChange={(e) => setB({ ...b, document_footer_text: e.target.value })} /></label>
            <label className="block text-sm text-charcoal-600">Extra notification recipients (one email per line)
              <span className="block text-xs text-charcoal-400 font-normal">Every admin notification (new bookings, contact messages, chat leads…) is also sent to these addresses — add your personal email so you never miss anything.</span>
              <textarea data-testid="brand-notification_emails" rows={3} className={`${FIELD} mt-1`}
                value={(b.notification_emails || []).join("\n")}
                onChange={(e) => setB({ ...b, notification_emails: e.target.value.split("\n") })}
                onBlur={(e) => setB({ ...b, notification_emails: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                placeholder={"you@gmail.com\npartner@gmail.com"} /></label>
          </div>
        </Card>

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Brand colors</h3>
          <p className="text-xs text-charcoal-500 mb-4">Used in email templates (buttons, accents, background).</p>
          <div className="flex flex-wrap gap-5">
            {Object.entries(b.colors || {}).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-charcoal-600 capitalize">
                <input type="color" data-testid={`brand-color-${k}`} value={v} onChange={(e) => setB({ ...b, colors: { ...b.colors, [k]: e.target.value } })}
                  className="h-9 w-12 cursor-pointer rounded border border-sand-300 bg-white" />
                {k}
              </label>
            ))}
          </div>
        </Card>

        {b.updated_at && <p className="text-xs text-charcoal-400">Last updated {b.updated_at.slice(0, 10)}</p>}
        <SaveButton testid="brand-save" onClick={save} saving={saving} />

        <Card>
          <h3 className="font-medium text-charcoal-800 mb-1">Database backup</h3>
          <p className="text-xs text-charcoal-500 mb-4">Download a full JSON backup of all data (bookings, clients, quotes, content, settings…). Keep a copy before any big change.</p>
          <button data-testid="db-export-button" onClick={downloadBackup} disabled={downloading}
            className="inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-medium text-white hover:bg-sage/90 disabled:opacity-60">
            <Download size={14} /> {downloading ? "Preparing backup…" : "Download database backup"}
          </button>
        </Card>
      </div>
    </div>
  );
}
